// Committees with public members (docs/architecture/BRE-Committees-Public-Members-Plan.md;
// ADR 0059). Builds on governance committees (ADR 0028), the myBRE board
// channel (ADR 0029) and the resolution book (ADR 0048):
//
//   - each committee's authority: it decides on its own (delegated by a
//     board resolution) or it recommends to the board;
//   - the committee register: every adopted committee decision numbered in
//     the committee's own series by fiscal year (RR-2027-01), never an R-
//     number, with links (amends, rescinds, supersedes) and a derived status;
//   - "Recommendation to the board": an adopted recommendation becomes a
//     proposed board motion, in the committee's wording, on the next regular
//     board meeting, under the committee's report;
//   - appointments: the chair recommends, the President approves;
//   - myBRE's Committees section for committee members, directors or not.
//
// The pure rules here are what Platform Core decides with; Hub's screens and
// myBRE only explain them.

import type { PersonRef } from './governance'
import { governanceFiscalYear } from './governance'
import type { BoardRecommendation, DerivedStatus, Ratification, ResolutionStatus } from './governance-resolutions'
import type { VoteTally } from './governance-decisions'

// ------------------------------------------------------------- authority

export const COMMITTEE_AUTHORITIES = ['recommends', 'decides'] as const
export type CommitteeAuthority = (typeof COMMITTEE_AUTHORITIES)[number]
export const COMMITTEE_AUTHORITY_LABELS: Record<CommitteeAuthority, string> = {
  decides: 'Decides on its own',
  recommends: 'Recommends to the board',
}
export const COMMITTEE_AUTHORITY_HELP: Record<CommitteeAuthority, string> = {
  decides: 'Its decisions don’t need the board’s approval.',
  recommends: 'Adopted recommendations go to the next regular board meeting.',
}

// ------------------------------------------------------------- the register

// A committee's series: one to six capital letters (RR, FIN). The board's
// R and the Executive Committee's EC are never a committee's; nor is H,
// which starts the series entered from records (H-R, H-EC).
export const REGISTER_PREFIX_PATTERN = /^[A-Z]{1,6}$/
export const RESERVED_REGISTER_PREFIXES = ['R', 'EC', 'H'] as const
export function registerPrefixProblem(prefix: string): string | null {
  if (!REGISTER_PREFIX_PATTERN.test(prefix)) return 'Use one to six capital letters, such as RR'
  if ((RESERVED_REGISTER_PREFIXES as readonly string[]).includes(prefix)) return `${prefix} is the board’s or the Executive Committee’s series; choose another`
  return null
}

const SKIP_WORDS = new Set(['the', 'and', 'of', 'for', 'a', 'an', 'committee', 'task', 'force', '&'])

// The prefix Hub uses when none was set: the committee name's initials
// ("Riverfront Rampage Committee" → RR), never a reserved one.
export function defaultRegisterPrefix(name: string): string {
  const words = name
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[^A-Za-z0-9 &]/g, ' ')
    .split(/\s+/)
    .filter((w) => w && !SKIP_WORDS.has(w.toLowerCase()))
  let prefix = words
    .map((w) => w[0]!.toUpperCase())
    .filter((ch) => /[A-Z]/.test(ch))
    .join('')
    .slice(0, 6)
  if (!prefix) prefix = 'C'
  if ((RESERVED_REGISTER_PREFIXES as readonly string[]).includes(prefix)) prefix = `${prefix}C`.slice(0, 6)
  return prefix
}

export const formatCommitteeDecisionNumber = (prefix: string, fiscalYear: number, seq: number) => `${prefix}-${fiscalYear}-${String(seq).padStart(2, '0')}`
export const committeeDecisionFiscalYear = (adoptedOn: string) => governanceFiscalYear(adoptedOn)

// What a later committee decision does to an earlier one of the same
// committee. (Ratifying is the board's.)
export const COMMITTEE_LINK_EFFECTS = ['amends', 'rescinds', 'supersedes'] as const
export type CommitteeLinkEffect = (typeof COMMITTEE_LINK_EFFECTS)[number]
export const COMMITTEE_LINK_LABELS: Record<CommitteeLinkEffect, string> = { amends: 'Amends', rescinds: 'Rescinds', supersedes: 'Supersedes' }

export const RECOMMENDATION_LABEL = 'Recommendation to the board'

// On a board motion that came from a committee: "Recommended by the Finance
// Committee (FC-2027-03)", or "… by the Riverfront Rampage committee (RR-…)".
export const recommendedByLabel = (committeeName: string, number: string) => `Recommended by the ${/\bcommittee$/i.test(committeeName.trim()) ? committeeName.trim() : `${committeeName.trim()} committee`} (${number})`

export type CommitteeDecisionListItem = {
  id: string
  number: string
  committee: { id: string; name: string }
  fiscalYear: number
  adoptedOn: string
  title: string
  executive: boolean
  redacted: boolean
  recommendation: boolean
  // The committee's authority when it was adopted.
  authority: CommitteeAuthority
  status: ResolutionStatus
  statusText: string
  meetingId: string | null
  meetingTitle: string | null
  snippet: string | null
  // A recommendation's board motion, once on a board agenda.
  board: CommitteeRecommendationBoard | null
  // ADR 0067: an EC- resolution subject to the board's ratification (the
  // Executive Committee's register only).
  ratification?: Ratification | null
  // ADR 0069: an EC- recommendation to the board, and where it stands (the
  // Executive Committee's register only).
  boardRecommendation?: BoardRecommendation | null
}

export type CommitteeRecommendationBoard = {
  motionId: string
  meetingId: string | null
  meetingTitle: string | null
  meetingStartsLocal: string | null
  status: string
  resolution: { id: string; number: string } | null
}

export type CommitteeDecisionDetail = CommitteeDecisionListItem & {
  text: string | null
  source: 'meeting' | 'email_meeting'
  motionId: string
  mover: PersonRef | null
  seconder: PersonRef | null
  tally: VoteTally | null
  votes: { person: PersonRef; choice: string }[]
  recusals: PersonRef[]
  derived: DerivedStatus
  acts: { id: string; effect: CommitteeLinkEffect; target: { id: string; number: string; title: string } }[]
  actedOnBy: { id: string; effect: CommitteeLinkEffect; by: { id: string; number: string; title: string }; lapsed: boolean }[]
  minutes: { id: string; status: string } | null
}

// ------------------------------------------------------------ appointments

export const APPOINTMENT_STATUSES = ['pending', 'approved', 'declined', 'withdrawn'] as const
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number]
export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = { pending: 'Waiting for the President', approved: 'Approved', declined: 'Declined', withdrawn: 'Withdrawn' }
export const APPOINTED_NOTE = 'Appointed by the President on the chair’s recommendation'

// Someone not in Hub yet, proposed by the chair from myBRE: Hub makes the
// person when the President approves (a BRE account through BRE Auth, or a
// record only).
export type ProposedNewPerson = { name: string; email: string | null; phone: string | null; account: 'bre_account' | 'record_only'; invite: boolean }

export type AppointmentRequestView = {
  id: string
  committee: { id: string; name: string }
  person: PersonRef | null
  newPerson: ProposedNewPerson | null
  voting: boolean
  startsOn: string
  endsOn: string | null
  note: string | null
  status: AppointmentStatus
  recommendedBy: PersonRef
  recommendedAt: string
  recommendedChannel: 'mybre' | 'hub' | 'attested'
  recommendAttestation: string | null
  decidedBy: PersonRef | null
  decidedAt: string | null
  decisionChannel: 'mybre' | 'hub' | 'attested' | null
  decisionAttestation: string | null
  decisionNote: string | null
  memberId: string | null
  // What approving it does about a BRE account invite (null until then).
  invite: string | null
}

// ------------------------------------------------- myBRE: the Committees section

export type CommitteeMembership = {
  id: string
  name: string
  kind: string
  role: 'chair' | 'member' | 'ex_officio_non_voting'
  voting: boolean
  authority: CommitteeAuthority
}

export type CommitteeMe = {
  isCommitteeMember: boolean
  committees: CommitteeMembership[]
  // The President approves appointments.
  isPresident: boolean
  // Voting members who aren't directors file the annual disclosure here.
  disclosureDue: boolean
}

// Labels for a committee member's role, in plain words.
export function committeeRoleText(m: { role: CommitteeMembership['role']; voting: boolean; derivedFrom?: string | null }): string {
  if (m.derivedFrom) return `${m.derivedFrom}${m.voting ? '' : ', non-voting'}`
  if (m.role === 'chair') return 'Chair'
  if (m.role === 'ex_officio_non_voting') return 'Ex officio, non-voting'
  return m.voting ? 'Member' : 'Member, non-voting'
}
